import styled, { css } from 'vue-styled-components';
import COLORS from '@design/colors.js';
import { FLEX_CENTER } from '@styles/common.styled';

import { darken } from 'polished';

import ClipLoader from 'vue-spinner/src/ClipLoader.vue';

const TYPES = {
    primary: css`
        background: ${COLORS['blue-60']};
        color: white;
        &:hover:enabled {
            background: ${darken(0.1, COLORS['blue-60'])};
        }
        &:active:enabled {
            background: ${darken(0.2, COLORS['blue-60'])};
        }
        &:disabled {
            background: ${COLORS['blue-30']};
        }
    `,
    secondary: css`
        background: ${COLORS['gray-15']};
        color: ${COLORS['gray-80']};

        &:hover:enabled {
            background: ${darken(0.1, COLORS['gray-15'])};
        }
        &:active:enabled {
            background: ${darken(0.2, COLORS['gray-15'])};
        }
    `,
    white: css`
        background: ${COLORS['white']};
        color: ${COLORS['gray-80']};
        border: solid 1px ${COLORS['gray-30']};
        &:hover:enabled {
            background: ${darken(0.05, COLORS['white'])};
        }
        &:active:enabled {
            background: ${darken(0.1, COLORS['white'])};
        }
    `,
};

const DISABLED = {
    primary: css`
        color: ${COLORS['blue-10']};
        cursor: default;
        background: ${COLORS['blue-30']};
        &:hover:enabled,
        &:active:enabled {
            background: ${COLORS['blue-30']};
        }
    `,
    secondary: css`
        background: white;
        border: solid 1px ${COLORS['gray-15']};
        color: ${COLORS['gray-30']};
        cursor: default;
        &:hover:enabled,
        &:active:enabled {
            background: white;
        }
    `,
    white: css`
        border: solid 1px #bdc2c7;
        background-color: white;
        color: #bdc2c7;
        cursor: default;
        &:hover:enabled,
        &:active:enabled {
            background: white;
        }
    `,
};

const OUTLINE = {
    secondary: css`
        border: solid 1px #bdc2c7;
    `,
};

const PROPS = {
    type: {
        type: String,
        default: 'primary',
    },
    size: {
        type: String,
        default: 'small',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    round: {
        type: Boolean,
        default: false,
    },
    width: String | Number,
    height: String | Number,
    circle: {
        type: Boolean,
        default: false,
    },
    outline: {
        type: Boolean,
        default: false,
    },
    color: {
        type: String,
    },
    css: {
        type: Array,
        default() {
            return css``;
        },
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    spinnerSize: {
        type: String,
        default: '20px',
    },
};

const Wrapper = styled('button', PROPS)`
    ${FLEX_CENTER}
    transition: background 0.2s;
    ${({ type }) => TYPES[type]}
    ${({ outline, type }) => outline && OUTLINE[type]}
    height: ${R.propOr('56px', 'height')};
    border-radius: ${({ round, circle }) => {
        if (circle) {
            return '50%';
        } else if (round) {
            return '24px';
        } else {
            return '2px';
        }
    }};
    ${({ color }) =>
        color
            ? css`
                  color: ${color};
              `
            : null};
    ${({ circle }) =>
        !circle &&
        css`
            & > img {
                margin-right: 8px;
                width: 16px;
            }
        `};
    ${({ disabled, type }) => disabled && DISABLED[type]};

    font-size: 18px;
    font-weight: 600;

    .v-spinner {
        ${FLEX_CENTER};
    }

    @media screen and (max-width: 767px) {
        height: 44px;
        width: 100%;
        font-size: 14px;
    }
`;

const PICKS = R.pick([
    'type',
    'round',
    'width',
    'height',
    'circle',
    'outline',
    'color',
]);

export default {
    name: 'CommonButton',
    props: PROPS,
    render() {
        const _Wrapper = styled(Wrapper)`
            ${this.css}
        `;

        return (
            <_Wrapper
                disabled={this.disabled}
                class={`adriel-btn ${this.type} ${
                    this.disabled ? 'disabled' : ''
                }`}
                {...{
                    attrs: PICKS(this),
                }}
                onClick={(e) => {
                    if (this.disabled) return;

                    this.$emit('click', e);
                }}
                onMousedown={(e) => this.$emit('mousedown', e)}
            >
                {this.isLoading ? this.renderLoading() : this.$slots.default}
            </_Wrapper>
        );
    },
    methods: {
        renderLoading() {
            let color;
            switch (this.type) {
                case 'primary':
                    color = 'white';
                    break;
            }

            return <ClipLoader color={color} size={this.spinnerSize} />;
        },
    },
};
